import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Spinner from '../../components/Spinner';

// Unity Editor 拡張「VRMA Hub Uploader」の紹介・ダウンロードページ。ログイン不要の公開ページ。
//
// パッケージは VCC（VRChat Creator Companion）向けの VPM リポジトリとして配信され、
// listing（/vpm/index.json）とパッケージ本体（zip）はサイトと同じ CloudFront から配信される。
// ローカル開発サーバにはこれらのファイルが存在しないため、取得に失敗した場合は
// 「デプロイ環境でのみ配信」の注記を表示する。

// VPM リポジトリ listing 内の、1 パッケージ・1 バージョン分の情報。
type VpmPackageVersion = {
  version: string;
  displayName?: string;
  url: string;
};

// GET /vpm/index.json のレスポンス形状。
type VpmListing = {
  name: string;
  id: string;
  url: string;
  packages?: Record<string, { versions?: Record<string, VpmPackageVersion> }>;
};

const PACKAGE_ID = 'com.vrmahub.uploader';

type DownloadState =
  | { status: 'loading' }
  | { status: 'ready'; version: VpmPackageVersion }
  | { status: 'unavailable' };

// "1.2.3" 形式のバージョン文字列を、ドット区切りの数値パートごとに比較する（プレリリース表記は考慮しない）。
function compareVersions(a: string, b: string): number {
  const partsA = a.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const partsB = b.split('.').map((n) => Number.parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const diff = (partsA[i] ?? 0) - (partsB[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

// listing から対象パッケージの最新バージョンを選ぶ。
function pickLatestVersion(listing: VpmListing): VpmPackageVersion | null {
  const versions = listing.packages?.[PACKAGE_ID]?.versions;
  if (!versions) return null;
  const values = Object.values(versions);
  if (values.length === 0) return null;
  return values.reduce((latest, v) => (compareVersions(v.version, latest.version) > 0 ? v : latest));
}

export default function UnityTool() {
  const origin = window.location.origin;
  const repoUrl = `${origin}/vpm/index.json`;
  const vccAddRepoUrl = `vcc://vpm/addRepo?url=${encodeURIComponent(repoUrl)}`;

  const [download, setDownload] = useState<DownloadState>({ status: 'loading' });
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState('');

  useEffect(() => {
    let alive = true;
    fetch('/vpm/index.json')
      .then((res) => {
        if (!res.ok) throw new Error(`status ${res.status}`);
        return res.json() as Promise<VpmListing>;
      })
      .then((listing) => {
        if (!alive) return;
        const version = pickLatestVersion(listing);
        setDownload(version ? { status: 'ready', version } : { status: 'unavailable' });
      })
      .catch(() => {
        // ローカル開発サーバなど、listing が存在しない環境ではここに来る。
        if (alive) setDownload({ status: 'unavailable' });
      });
    return () => {
      alive = false;
    };
  }, []);

  const copyRepoUrl = async () => {
    try {
      await navigator.clipboard.writeText(repoUrl);
      setCopied(true);
    } catch {
      setCopyError('コピーに失敗しました。手動で選択してコピーしてください。');
    }
  };

  return (
    <div className="container">
      <div className="section-title">
        <h2>Unity ツール</h2>
      </div>

      <p>
        Unity Editor から Humanoid の AnimationClip を VRMA
        に変換し、そのまま VRMA Hub に投稿できる公式 Editor 拡張「VRMA Hub Uploader」を配布しています。
        プロジェクトに追加すると、Unity に専用のアップローダーメニューが追加されます。
      </p>

      <section>
        <h3>インストール</h3>
        <p>インストール方法は次の3通りです。</p>
      </section>

      <section>
        <h3>① VCC の「VCC に追加」ボタンから登録する</h3>
        <p>下のボタンから直接リポジトリを追加できます。</p>
        <p>
          <a href={vccAddRepoUrl} className="btn btn--primary">
            VCC に追加
          </a>
        </p>
        <ol>
          <li>対象プロジェクトの「Manage Project」を開き、「VRMA Hub Uploader」を追加してください。</li>
        </ol>
      </section>

      <section>
        <h3>② リポジトリ URL を VCC に手動登録する</h3>
        <p>
          リポジトリ URL:
          <br />
          <code className="code-inline">{repoUrl}</code>{' '}
          <button type="button" className="btn" onClick={copyRepoUrl}>
            {copied ? 'コピーしました' : 'コピー'}
          </button>
        </p>
        {copyError && <div className="viewer__error">{copyError}</div>}
        <ol>
          <li>VCC の Settings &gt; Packages &gt; Add Repository に上記の URL を貼り付けてください。</li>
          <li>対象プロジェクトの「Manage Project」を開き、「VRMA Hub Uploader」を追加してください。</li>
        </ol>
      </section>

      <section>
        <h3>③ zip をダウンロードして導入する</h3>
        {download.status === 'ready' ? (
          <p>
            最新バージョン: v{download.version.version}
            <br />
            <a href={download.version.url}>
              com.vrmahub.uploader-{download.version.version}.zip をダウンロード
            </a>
          </p>
        ) : download.status === 'unavailable' ? (
          <p style={{ color: 'var(--color-muted)', fontSize: 13 }}>
            パッケージはデプロイ環境でのみ配信されます。
          </p>
        ) : (
          <Spinner inline size="sm" />
        )}
      </section>

      <section>
        <h3>使い方</h3>
        <ol>
          <li>Unity（2022.3 以降）でプロジェクトを開き、Tools &gt; VRMA Hub Uploader を開きます。</li>
          <li>VRMA Hub のメールアドレスとパスワードでログインします。</li>
          <li>
            Humanoid の AnimationClip を選択するか、既存の .vrma / .glb ファイルを指定して、タイトルなどを入力します。
          </li>
          <li>アップロードすると、マイページと詳細ページにすぐに反映されます。</li>
        </ol>
      </section>

      <section>
        <h3>注意事項</h3>
        <ul>
          <li>対応しているのは Humanoid の AnimationClip のみです。</li>
          <li>指・表情・視線のボーンやブレンドシェイプには対応していません。</li>
          <li>
            アップロードには VRMA Hub のアカウントが必要です。未登録の方は<Link to="/signup">新規登録</Link>
            してください。
          </li>
          <li>
            投稿する作品は<Link to="/guidelines">コミュニティガイドライン</Link>に従ってください。
          </li>
        </ul>
      </section>
    </div>
  );
}
